import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  BedDouble,
  Bath,
  Users,
  ArrowLeft,
  Landmark,
  TrainFront,
  Check,
  Loader2,
  ClipboardList,
  Wrench,
  BarChart3,
} from "lucide-react";
import ImageGallery from "../components/ImageGallery";
import InquiryForm from "../components/InquiryForm";
import { fetchApartment } from "../lib/api";
import { AMENITY_ICONS } from "../lib/amenities";

export default function CaseStudy() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [apartment, setApartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchApartment(id)
      .then(setApartment)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (apartment) document.title = `${apartment.name} — Case Study · Sagita Rasmadi LLC`;
    return () => {
      document.title = "Sagita Rasmadi LLC";
    };
  }, [apartment]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="apartment-detail-loading">
        <Loader2 className="animate-spin text-[#7A8C7B]" />
      </div>
    );
  }

  if (notFound || !apartment) {
    return (
      <div className="container-luxe py-32 text-center" data-testid="apartment-not-found">
        <h2 className="font-[Cormorant_Garamond] text-4xl text-[#1A1D1A]">
          Case study not found
        </h2>
        <button
          onClick={() => navigate("/portfolio")}
          className="btn-primary mt-8"
          data-testid="not-found-back"
        >
          <ArrowLeft size={16} />
          Back to portfolio
        </button>
      </div>
    );
  }

  return (
    <div data-testid={`apartment-detail-page-${apartment.id}`} className="pb-32">
      {/* HERO IMAGE */}
      <section className="container-luxe pt-6">
        <Link
          to="/portfolio"
          data-testid="detail-back-link"
          className="mb-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#4A524C] transition hover:text-[#7A8C7B]"
        >
          <ArrowLeft size={14} />
          Back to portfolio
        </Link>

        <motion.div
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden rounded-[28px] shadow-[0_30px_60px_rgba(26,29,26,0.12)]"
        >
          <div className="aspect-[16/9] w-full bg-[#D9CFC2]">
            <img
              src={apartment.images[0]}
              alt={apartment.name}
              className="h-full w-full object-cover"
            />
          </div>
        </motion.div>
      </section>

      {/* HEADER */}
      <section className="container-luxe pt-12">
        <div className="flex flex-col items-start gap-8 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="overline" data-testid="detail-overline">
              Case Study · Managed on behalf of the owner
            </span>
            <h1 className="mt-4 font-[Cormorant_Garamond] text-5xl text-[#1A1D1A] md:text-6xl">
              {apartment.name}
            </h1>
            <p className="mt-3 inline-flex items-center gap-2 text-sm text-[#4A524C]">
              <MapPin size={14} className="text-[#C6A66B]" />
              {apartment.address}
            </p>
            <p className="mt-4 max-w-2xl font-[Cormorant_Garamond] text-2xl italic text-[#4A524C]">
              {apartment.tagline}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 md:gap-6">
            <Stat icon={BedDouble} label="Bedrooms" value={apartment.bedrooms} />
            <Stat icon={Bath} label="Bathrooms" value={apartment.bathrooms} />
            <Stat icon={Users} label="Sleeps" value={apartment.guests} />
          </div>
        </div>
      </section>

      {/* OUTCOME METRICS */}
      {apartment.outcome_metrics?.length > 0 && (
        <section className="container-luxe mt-14">
          <div className="grid gap-6 rounded-3xl bg-[#7A8C7B] px-8 py-10 text-white md:grid-cols-3">
            {apartment.outcome_metrics.map((m) => (
              <div
                key={m.label}
                data-testid={`outcome-${m.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                className="text-center md:text-left"
              >
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/70">
                  {m.label}
                </div>
                <div className="mt-2 font-[Cormorant_Garamond] text-4xl">
                  {m.value}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* GALLERY */}
      <section className="container-luxe mt-14">
        <ImageGallery images={apartment.images} alt={apartment.name} />
      </section>

      {/* MAIN CONTENT */}
      <section className="container-luxe mt-20 grid gap-14 lg:grid-cols-12">
        <div className="space-y-16 lg:col-span-7">
          {/* Owner brief */}
          {apartment.owner_brief && (
            <div data-testid="case-owner-brief">
              <div className="inline-flex items-center gap-2 text-[#967A45]">
                <ClipboardList size={16} />
                <span className="overline">The Owner Brief</span>
              </div>
              <h2 className="mt-3 font-[Cormorant_Garamond] text-3xl text-[#1A1D1A] md:text-4xl">
                What the owner came to us with
              </h2>
              <div className="mt-5 h-px w-12 bg-[#C6A66B]" />
              <p className="mt-7 text-base leading-relaxed text-[#4A524C] md:text-lg">
                {apartment.owner_brief}
              </p>
            </div>
          )}

          {/* Our approach */}
          {apartment.our_approach?.length > 0 && (
            <div data-testid="case-our-approach">
              <div className="inline-flex items-center gap-2 text-[#967A45]">
                <Wrench size={16} />
                <span className="overline">Our Approach</span>
              </div>
              <h3 className="mt-3 font-[Cormorant_Garamond] text-3xl text-[#1A1D1A]">
                Scope of the mandate
              </h3>
              <div className="mt-5 h-px w-12 bg-[#C6A66B]" />
              <p className="mt-6 text-base leading-relaxed text-[#4A524C]">
                {apartment.long_description}
              </p>
              <ul className="mt-8 space-y-3">
                {apartment.our_approach.map((step, i) => (
                  <li
                    key={step}
                    className="flex items-start gap-4 rounded-xl border border-[#D9CFC2]/60 bg-white px-5 py-4"
                  >
                    <span className="font-[Cormorant_Garamond] text-xl italic text-[#C6A66B]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm text-[#1A1D1A]">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Property specification */}
          <div>
            <div className="inline-flex items-center gap-2 text-[#967A45]">
              <BarChart3 size={16} />
              <span className="overline">Property Specification</span>
            </div>
            <h3 className="mt-3 font-[Cormorant_Garamond] text-3xl text-[#1A1D1A]">
              What is included in the managed operating standard
            </h3>
            <div className="mt-5 h-px w-12 bg-[#C6A66B]" />
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {apartment.amenities.map((a) => {
                const Icon = AMENITY_ICONS[a] || Check;
                return (
                  <div
                    key={a}
                    data-testid={`amenity-${a.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center gap-3 rounded-xl border border-[#D9CFC2]/60 bg-white/60 px-4 py-3.5"
                  >
                    <div className="grid h-10 w-10 place-items-center rounded-full bg-[#F7F4ED] text-[#7A8C7B]">
                      <Icon size={18} />
                    </div>
                    <span className="text-sm font-medium text-[#1A1D1A]">
                      {a}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Nearby */}
          <div className="grid gap-10 md:grid-cols-2">
            <div data-testid="nearby-attractions">
              <div className="inline-flex items-center gap-2 text-[#967A45]">
                <Landmark size={16} />
                <span className="overline">Neighbourhood</span>
              </div>
              <ul className="mt-5 space-y-3">
                {apartment.nearby_attractions.map((a) => (
                  <li
                    key={a}
                    className="flex items-start gap-3 text-sm text-[#4A524C]"
                  >
                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#C6A66B]" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
            <div data-testid="transport-info">
              <div className="inline-flex items-center gap-2 text-[#967A45]">
                <TrainFront size={16} />
                <span className="overline">Transport</span>
              </div>
              <ul className="mt-5 space-y-3">
                {apartment.transport.map((t) => (
                  <li
                    key={t}
                    className="flex items-start gap-3 text-sm text-[#4A524C]"
                  >
                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#7A8C7B]" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* INQUIRY (owner-oriented) */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <InquiryForm
              apartment={apartment}
              source={`case-study:${apartment.id}`}
              title="Discuss a similar mandate"
              subtitle="Own a comparable property? Share a few details and our practice lead will be in touch to explore whether a similar mandate could work for you."
            />
          </div>
        </aside>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div
      data-testid={`stat-${label.toLowerCase()}`}
      className="rounded-2xl border border-[#D9CFC2]/60 bg-white px-5 py-4 text-center shadow-[0_4px_18px_rgba(26,29,26,0.03)]"
    >
      <Icon size={18} className="mx-auto text-[#C6A66B]" />
      <p className="mt-2 font-[Cormorant_Garamond] text-2xl text-[#1A1D1A]">
        {value}
      </p>
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#7A8C7B]">
        {label}
      </p>
    </div>
  );
}
